"use client";

import { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { api } from "@/lib/api";
import type { Source, Target, RouteItem, RouteStep } from "@/lib/types/routing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StepEditor } from "@/components/routes/step-editor";
import { useAuthStore } from "@/lib/stores/auth-store";

export type PanelMode = "create" | "edit";

export interface PanelState {
  mode: PanelMode;
  /** prefilled for create; null means "none yet" */
  sourceId: string;
  targetIds: string[];
  routeId?: string;
  routeName?: string;
  steps?: RouteStep[];
}

interface RouteSidePanelProps {
  open: boolean;
  state: PanelState | null;
  sources: Source[];
  targets: Target[];
  onClose: () => void;
  onSaved: (route: RouteItem) => void;
  onDeleted: (routeId: string) => void;
}

export function RouteSidePanel({
  open,
  state,
  sources,
  targets,
  onClose,
  onSaved,
  onDeleted,
}: RouteSidePanelProps) {
  const t = useTranslations();
  const { user } = useAuthStore();
  const userPlan = user?.plan ?? "Expired";

  const [name, setName] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [targetIds, setTargetIds] = useState<string[]>([]);
  const [steps, setSteps] = useState<RouteStep[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Sync form state when panel opens
  useEffect(() => {
    if (!state) return;
    setName(state.routeName ?? "");
    setSourceId(state.sourceId ?? "");
    setTargetIds(state.targetIds ?? []);
    setSteps(state.steps ?? []);
    setConfirmDelete(false);
  }, [state]);

  const toggleTarget = (id: string) => {
    setTargetIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!sourceId) {
      toast.error("Source is required");
      return;
    }
    if (targetIds.length === 0) {
      toast.error("At least one destination is required");
      return;
    }
    setSubmitting(true);
    try {
      let route: RouteItem;
      if (state?.mode === "edit" && state.routeId) {
        route = await api.routes.update(state.routeId, {
          name: name.trim(),
          targetIds,
          steps,
        });
        toast.success(t("dashboard.routes.routeUpdated"));
      } else {
        route = await api.routes.create({
          name: name.trim(),
          sourceId,
          targetIds,
          steps,
        });
        toast.success(t("dashboard.routes.routeCreated"));
      }
      onSaved(route);
      onClose();
    } catch {
      toast.error("Failed to save flow");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!state?.routeId) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await api.routes.delete(state.routeId);
      toast.success(t("dashboard.routes.routeDeleted"));
      onDeleted(state.routeId);
      onClose();
    } catch {
      toast.error("Failed to delete flow");
    } finally {
      setDeleting(false);
    }
  };

  if (!open || !state) return null;

  const isEdit = state.mode === "edit";
  const isCreateMode = state.mode === "create";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        className="fixed inset-e-0 top-0 z-50 flex h-full w-full max-w-[420px] flex-col border-s border-border bg-background shadow-xl"
        role="dialog"
        aria-label={isEdit ? t("dashboard.flow.editRouteTitle") : t("dashboard.flow.createRouteTitle")}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold">
            {isEdit ? t("dashboard.flow.editRouteTitle") : t("dashboard.flow.createRouteTitle")}
          </h2>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="rsp-name">{t("dashboard.routes.nameLabel")}</Label>
            <Input
              id="rsp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("dashboard.routes.namePlaceholder")}
            />
          </div>

          {/* Source */}
          <div className="space-y-1.5">
            <Label htmlFor="rsp-source">{t("dashboard.routes.sourceLabel")}</Label>
            {isCreateMode && state.sourceId ? (
              // locked in create-from-drag mode
              <div className="border border-border bg-muted/30 px-3 py-2 text-sm">
                {sources.find((s) => s.id === state.sourceId)?.name ??
                  state.sourceId}
              </div>
            ) : (
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger id="rsp-source">
                  <SelectValue placeholder={t("dashboard.routes.sourcePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Targets */}
          <div className="space-y-1.5">
            <Label>{t("dashboard.routes.targetsLabel")}</Label>
            <div className="space-y-1">
              {targets.map((tgt) => {
                const selected = targetIds.includes(tgt.id);
                return (
                  <button
                    key={tgt.id}
                    type="button"
                    onClick={() => toggleTarget(tgt.id)}
                    className={
                      "flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors " +
                      (selected
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-border bg-transparent text-muted-foreground hover:border-primary/40 hover:text-foreground")
                    }
                  >
                    <span className="truncate">{tgt.name}</span>
                    {selected && (
                      <span className="ms-2 shrink-0 text-primary">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-1.5">
            <Label>{t("dashboard.routes.stepsLabel")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("dashboard.routes.stepsDescription")}
            </p>
            <StepEditor
              steps={steps}
              userPlan={userPlan}
              onChange={setSteps}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 border-t border-border px-5 py-4">
          {isEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleting}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="me-1.5 h-3.5 w-3.5" />
              {confirmDelete ? t("dashboard.routes.deleteConfirm").slice(0, 20) + "…?" : t("dashboard.routes.deleteButton")}
            </Button>
          )}
          <div className="ms-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              {t("dashboard.routes.cancel")}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={submitting}>
              {submitting
                ? isEdit
                  ? t("dashboard.routes.saving")
                  : t("dashboard.routes.creating")
                : isEdit
                ? t("dashboard.routes.save")
                : t("dashboard.routes.create")}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}
